import { HttpErrorResponse } from '@angular/common/http'
import { EntityState, Update } from '@ngrx/entity'
import { Action, ActionCreator } from '@ngrx/store'
import { ImagesEntity } from 'libs/gb-dashboard/shared/data-access-images/src'
import { UndoRedoState } from './undo-redo.models'

// Base Widget Interface (shared by all widget types)
export interface BaseWidget {
  id?: number
  uuid: string
  is_active?: boolean
  created?: string
  updated?: string
  projector?: string
  projector_id?: number | null
  x?: number
  x_unit?: string
  y?: number
  y_unit?: string
  z?: number
  rotate_z?: number | null
  rotate_z_unit?: string
  width?: number | null
  width_unit?: string
  height?: number | null
  height_unit?: string
  background_color?: string
  primary_color?: string
  primary_text_color?: string
  opacity?: number | null
  border_color?: string
  border_width?: number | null
  border_width_unit?: string
  border_style?: string
  border_radius?: number | null
  border_radius_unit?: string
  padding?: number | null
  padding_unit?: string
  box_shadow?: string

  // Font Control Values
  font_family?: string
  font_weight?: string
  font_size?: number
  color?: string
  text_align?: string
}

// Countdown Widgets
export interface CountdownWidget extends BaseWidget {
  timer_type?: string
  letter_spacing?: number
  timeout_text?: string
  plus_overtime_label?: boolean
  end_on_auction_schedule_close?: boolean
  end?: string
}

export type CountdownWidgetModel = CountdownWidget

// Image Widgets
export enum ImageWidgetSourceEnum {
  Custom = 'custom',
  AuctionBanner = 'auction_banner',
  AuctionLogo = 'auction_logo',
}

export interface ImageWidget extends BaseWidget {
  image?: ImagesEntity | null
  image_id?: number | null
  image_source?: ImageWidgetSourceEnum | null
}

export type ImageWidgetModel = ImageWidget

// Text Widgets
export interface TextWidget extends BaseWidget {
  text?: string
  font_style?: string
  text_decoration?: string
  line_height?: number
  letter_spacing?: number
}

export type TextWidgetModel = TextWidget

// Smart Text Widgets
export interface SmartTextWidget extends BaseWidget {
  text?: string
}

export type SmartTextWidgetModel = SmartTextWidget

// Total Raised Widgets
export interface TotalRaisedWidget extends BaseWidget {
  label?: string
  total_raised?: string

  track_bids?: boolean
  track_donations?: boolean
  track_purchases?: boolean
  track_raffles?: boolean
  track_ticket_sales?: boolean
  track_paddle_raise_pledges?: boolean
  base_total_raised_enabled?: boolean
  hidden_items?: string[]
}

export type TotalRaisedWidgetModel = TotalRaisedWidget

// Team Scoring Widgets
export interface TeamScore {
  uuid: string
  name: string
  order: number
  score: string
}

export interface TeamScoringWidget extends BaseWidget {
  columns?: number
  rows?: number
  slide_interval?: number
  table_primary_color?: string
  table_accent_color?: string
  table_background_color?: string | null
  card_type?: string
  hidden_teams?: string[]
  transition?: string
  team_scores?: TeamScore[]
}

export type TeamScoringWidgetModel = TeamScoringWidget

// Power Pledge Widgets
export interface PowerPledgeEntry {
  uuid: string
  name: string
  total: number
}

export interface PowerPledgeWidget extends BaseWidget {
  rows?: number
  table_primary_color?: string
  table_accent_color?: string
  first_place_color?: string
  second_place_color?: string
  third_place_color?: string
  default_rank_color?: string
  donor_totals?: PowerPledgeEntry[]
}

export type PowerPledgeWidgetModel = PowerPledgeWidget

// Notification Widgets
export interface NotificationWidget extends BaseWidget {
  notif_type?: NotifTypeEnum
  notif_secondary_color?: string
  notif_secondary_background_color?: string
  show_bid_notifications?: boolean
  show_purchase_notifications?: boolean
  show_raffle_notifications?: boolean
  show_bid_purchase_notifications?: boolean
  show_donation_notifications?: boolean
  show_custom_notifications?: boolean
  hide_name?: boolean
  hide_price?: boolean
  image_contained?: boolean
}

export type NotificationWidgetModel = NotificationWidget

export interface ProjectorNotificationData {
  image: string | null
  image_url: string | null
  image_cover?: boolean | null
  header: string
  body: string
  secondary_text_1: string
  secondary_text_2: string
  font_icon: string | null
  notif_event: string
}

// Item Table Widgets
export enum ItemTableCardType {
  STANDARD_TABLE = 'standard_table',
  COMPACT_TABLE = 'compact_table',
  TALL_CARD = 'tall_card',
}

export interface ItemTableWidget extends BaseWidget {
  columns?: number
  rows?: number
  slide_interval?: number
  table_primary_color?: string
  table_accent_color?: string
  table_background_color?: string
  card_type?: ItemTableCardType
  hidden_items?: string[]
  transition?: string
  image_contained?: boolean
  show_item_type?: boolean
  item_name_max_lines?: number | null
}

export type ItemTableWidgetModel = ItemTableWidget

// Raffle Winners Widgets
export interface RaffleWinner {
  uuid: string
  winner_name: string
  prize_name: string
  ticket_number: string
}

export interface RaffleWinnersWidget extends BaseWidget {
  title?: string
  raffle_item?: string | null
  show_ticket_numbers?: boolean
  show_prize_names?: boolean
  animation_type?: string
  slide_interval?: number
  accent_color?: string
  title_font_size?: number
  font_color?: string
  prizes_available?: number
  title_font_color?: string
  background_color_override?: string | null
  rows?: number
  columns?: number
  winners?: RaffleWinner[]
}

export type RaffleWinnersWidgetModel = RaffleWinnersWidget

// Honour Roll Widgets
export interface HonourRollEntry {
  uuid: string
  name: string
  amount: number
  message: string | null
}

export enum HonorRollLayoutDirectionEnum {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export interface HonourRollWidget extends BaseWidget {
  columns?: number
  rows?: number
  layout_direction?: HonorRollLayoutDirectionEnum
  slide_interval?: number
  show_item_name?: boolean
  show_item_alias?: boolean
  show_bidding?: boolean
  show_live?: boolean
  show_raffle?: boolean
  show_purchase?: boolean
  show_donation?: boolean
  show_bidding_purchase?: boolean
  table_primary_color?: string
  table_accent_color?: string
  table_background_color?: string
  transition?: string
  latest_activities?: number
  hidden_items?: string[]
}

export type HonourRollWidgetModel = HonourRollWidget

// Thermometer Widgets
export interface ThermometerWidget extends BaseWidget {
  amount_raised?: number
  fundraising_progress?: number
  fundraising_target?: number
  track_pledges_only?: boolean
  base_total_raised_enabled?: boolean

  thermometer_background_color?: string
  thermometer_border_color?: string
  thermometer_color?: string

  custom_image?: ImagesEntity | null
  custom_image_id?: number | null
  hidden_items?: string[]
}

export interface HonourRollActivity {
  uuid: string
  item_alias: string
  item_name: string
  item_type: string
  is_bidding_purchase: boolean
  profile_full_name: string
  quantity: number | null
  amount: number
  created: string
  widget_uuid: string
  item_image?: ImagesEntity | null
}

export type ThermometerWidgetModel = ThermometerWidget

// Raffle Draw Widgets
export interface RaffleDrawEntry {
  uuid: string
  participant_name: string
  ticket_number: string
  is_winner: boolean
}

export interface RaffleDrawWidget extends BaseWidget {
  title?: string
  raffle_item?: string | null
  animation_type?: string
  animation_duration?: number
  enable_confetti?: boolean
  enable_bulk_raffle_draw?: boolean
  show_ticket_numbers?: boolean
  show_prize_numbers?: boolean
}

export type RaffleDrawWidgetModel = RaffleDrawWidget

// QR Code Widgets
export interface QrCodeWidget extends BaseWidget {
  url?: string
  hide_url?: boolean
}

export type QrCodeWidgetModel = QrCodeWidget

// Video Widgets
export interface VideoWidget extends BaseWidget {
  video_url?: string
}

export type VideoWidgetModel = VideoWidget

// Union type for all widget models
export type WidgetModel =
  | CountdownWidgetModel
  | ImageWidgetModel
  | TextWidgetModel
  | SmartTextWidgetModel
  | TotalRaisedWidgetModel
  | TeamScoringWidgetModel
  | PowerPledgeWidgetModel
  | NotificationWidgetModel
  | ItemTableWidgetModel
  | RaffleWinnersWidgetModel
  | HonourRollWidgetModel
  | ThermometerWidgetModel
  | RaffleDrawWidgetModel
  | QrCodeWidgetModel
  | VideoWidgetModel

// Widget type enum
export enum WidgetType {
  COUNTDOWN = 'countdown',
  IMAGE = 'image',
  TEXT = 'text',
  SMART_TEXT = 'smart-text',
  TOTAL_RAISED = 'total-raised',
  TEAM_SCORING = 'team-scoring',
  POWER_PLEDGE = 'power-pledge',
  NOTIFICATION = 'notification',
  ITEM_TABLE = 'item-table',
  RAFFLE_WINNERS = 'raffle-winners',
  HONOUR_ROLL = 'honour-roll',
  THERMOMETER = 'thermometer',
  RAFFLE_DRAW = 'raffle-draw',
  QR_CODE = 'qr-code',
  VIDEO = 'video',
}

export enum PresenterToolsEnum {
  COUNTDOWN = 'countdown',
  IMAGE = 'image',
  TEXT = 'text',
  TOTAL_RAISED = 'total-raised',
  TEAM_SCORING = 'team-scoring',
  POWER_PLEDGE = 'power-pledge',
  NOTIFICATION = 'notification',
  ITEM_TABLE = 'item-table',
  RAFFLE_WINNERS = 'raffle-winners',
  HONOUR_ROLL = 'honour-roll',
  THERMOMETER = 'thermometer',
  RAFFLE_DRAW = 'raffle-draw',
  QR_CODE = 'qr-code',
  VIDEO = 'video',
  OPACITY = 'opacity',
  LAYOUT = 'layout',
}

export enum NotifTypeEnum {
  POPOUT_A = 'popout_a',
  POPOUT_B = 'popout_b',
  TOAST_A = 'toast_a',
}

export interface WidgetModelMap {
  [WidgetType.COUNTDOWN]: CountdownWidgetModel
  [WidgetType.IMAGE]: ImageWidgetModel
  [WidgetType.TEXT]: TextWidgetModel
  [WidgetType.SMART_TEXT]: SmartTextWidgetModel
  [WidgetType.TOTAL_RAISED]: TotalRaisedWidgetModel
  [WidgetType.TEAM_SCORING]: TeamScoringWidgetModel
  [WidgetType.POWER_PLEDGE]: PowerPledgeWidgetModel
  [WidgetType.NOTIFICATION]: NotificationWidgetModel
  [WidgetType.ITEM_TABLE]: ItemTableWidgetModel
  [WidgetType.RAFFLE_WINNERS]: RaffleWinnersWidgetModel
  [WidgetType.HONOUR_ROLL]: HonourRollWidgetModel
  [WidgetType.THERMOMETER]: ThermometerWidgetModel
  [WidgetType.RAFFLE_DRAW]: RaffleDrawWidgetModel
  [WidgetType.QR_CODE]: QrCodeWidgetModel
  [WidgetType.VIDEO]: VideoWidgetModel
}

export type WidgetConfigMap = {
  [K in WidgetType]: {
    entityKey: string
    methods: {
      create: string
      update: string
      delete: string
    }
    actions: {
      create: {
        success: ActionCreator<string, (props: { widget: WidgetModelMap[K] }) => { widget: WidgetModelMap[K] } & Action>
        failure: ActionCreator<string, (props: { error: HttpErrorResponse }) => { error: HttpErrorResponse } & Action>
      }
      update: {
        success: ActionCreator<
          string,
          (props: { update: Update<WidgetModelMap[K]> }) => { update: Update<WidgetModelMap[K]> } & Action
        >
        failure: ActionCreator<string, (props: { error: HttpErrorResponse }) => { error: HttpErrorResponse } & Action>
      }
      delete: {
        success: ActionCreator<string, (props: { uuid: string }) => { uuid: string } & Action>
        failure: ActionCreator<string, (props: { error: HttpErrorResponse }) => { error: HttpErrorResponse } & Action>
      }
    }
    selectEntities: (state: PresenterWidgetsState) => WidgetModelMap[K][]
  }
}

export const PRESENTER_WIDGETS_FEATURE_KEY = 'presenterWidgets'

export interface CountdownWidgetsState extends EntityState<CountdownWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, CountdownWidgetModel>
}

export interface ImageWidgetsState extends EntityState<ImageWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, ImageWidgetModel>
}

export interface TextWidgetsState extends EntityState<TextWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, TextWidgetModel>
}

export interface SmartTextWidgetsState extends EntityState<SmartTextWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, SmartTextWidgetModel>
}

export interface TotalRaisedWidgetsState extends EntityState<TotalRaisedWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, TotalRaisedWidgetModel>
}

export interface TeamScoringWidgetsState extends EntityState<TeamScoringWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, TeamScoringWidgetModel>
}

export interface PowerPledgeWidgetsState extends EntityState<PowerPledgeWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, PowerPledgeWidgetModel>
}

export interface NotificationWidgetsState extends EntityState<NotificationWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, NotificationWidgetModel>
}

export interface ItemTableWidgetsState extends EntityState<ItemTableWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, ItemTableWidgetModel>
}

export interface RaffleWinnersWidgetsState extends EntityState<RaffleWinnersWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, RaffleWinnersWidgetModel>
}

export interface HonourRollWidgetsState extends EntityState<HonourRollWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, HonourRollWidgetModel>
}

export interface ThermometerWidgetsState extends EntityState<ThermometerWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, ThermometerWidgetModel>
}

export interface RaffleDrawWidgetsState extends EntityState<RaffleDrawWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, RaffleDrawWidgetModel>
}

export interface QrCodeWidgetsState extends EntityState<QrCodeWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, QrCodeWidgetModel>
}

export interface VideoWidgetsState extends EntityState<VideoWidgetModel> {
  selectedId?: string | number
  loaded: boolean
  error?: HttpErrorResponse | null
  editCreateLoaded: boolean
  createLoaded: boolean
  optimisticDeleted: Record<string, VideoWidgetModel>
}

export interface PresenterWidgetsState {
  countdownWidgets: CountdownWidgetsState
  imageWidgets: ImageWidgetsState
  textWidgets: TextWidgetsState
  smartTextWidgets: SmartTextWidgetsState
  totalRaisedWidgets: TotalRaisedWidgetsState
  teamScoringWidgets: TeamScoringWidgetsState
  powerPledgeWidgets: PowerPledgeWidgetsState
  notificationWidgets: NotificationWidgetsState
  itemTableWidgets: ItemTableWidgetsState
  raffleWinnersWidgets: RaffleWinnersWidgetsState
  honourRollWidgets: HonourRollWidgetsState
  thermometerWidgets: ThermometerWidgetsState
  raffleDrawWidgets: RaffleDrawWidgetsState
  qrCodeWidgets: QrCodeWidgetsState
  videoWidgets: VideoWidgetsState
  selectedWidget: WidgetModel | null
  selectedWidgetType: WidgetType | null
  savingWidget: Partial<WidgetModel> | null | undefined
}

export interface PresenterWidgetsPartialState {
  readonly [PRESENTER_WIDGETS_FEATURE_KEY]: UndoRedoState<PresenterWidgetsState>
}
